import { motion } from 'framer-motion'
import { PlanCard } from '@/components/pricing-plans/PlanCard.jsx'
import { usePaddle } from '@/hooks/usePaddle.jsx'
import { handleError } from '@/services/errorHandler.js'
import { toast } from 'sonner'
import { cancelSubscription, getSubscriptionOfAccount } from '@/services/api.js'
import { useEffect, useState } from 'react'
import { usePaymentContext } from '@/context-state/PaymentContext.jsx'
import { useCurrentAccount } from '@/hooks/useCurrentAccount.jsx'
import { TypographySmall } from '@/components/ui/typography.jsx'
import { Icon } from '@iconify/react'
import { cn } from '@/lib/utils'
import { CircleLoader } from '@/components/loader/CircleLoader.jsx'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx'
import { Button } from '@/components/ui/button.jsx'
import { toggleVerifyPayment } from '@/hooks/useVerifyPayment.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { AnalyticsService } from '@/services/analytics.js'
import * as API from '@/services/api.js'
import { useSyncEntitlements } from '@/hooks/useSyncEntitlements'
import { togglePricingPlansDialog } from '@/hooks/usePricingPlans'

const generateRandomId = (length = 8) => {
    let id = ''
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    for (let i = 0; i < length; i++) {
        id += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return id
}

// Best user-facing message from a payment error. The backend's real message
// (e.g. a Paddle card-decline reason) rides on response.data.error.message; the
// axios error's own message is often empty or a generic "Request failed…", so
// fall back to a friendly, action-oriented default rather than toast nothing.
const paymentErrorMessage = (e, fallback) => e?.response?.data?.error?.message || e?.message || fallback


export const PricingPlans = ({ onCheckoutOpened, onSubscriptionCancelled, autoOpenCustomCheckout = true }) => {


    const [isCancellingSubscription, setIsCancelingSubscription] = useState(false)
    const [isApproveCancelDialogOpen, setIsApproveCancelDialogOpen] = useState(false)
    const [chosenPlanToUpgrade, setChosenPlanToUpgrade] = useState(null)

    const [cycle, setCycle] = useState('annually')
    const { plans, setSubscription, subscription } = usePaymentContext()
    const syncEntitlements = useSyncEntitlements()
    const { account } = useCurrentAccount()
    const { user } = useAuthContext()
    const transactionId = account?.customCheckout
    const isShopify = account?.type === 'store' && account?.ecomProvider === 'shopify'

    const handlePaddleEvents = (event) => {
        if (event.name === 'checkout.loaded') {
            AnalyticsService.track('checkout-opened', event.data)
        }

        if (event.name === 'checkout.payment.initiated') {
            AnalyticsService.track('checkout-payment-initialized', event.data)
        }

        if (event.name === 'checkout.closed') {
            if (event.data.status === 'completed') {
                AnalyticsService.track('checkout-closed-on-complete', event.data)
                toggleVerifyPayment(true)
                return
            }
            AnalyticsService.track('checkout-closed-before-complete', event.data)
        }
    }

    const paddleClient = usePaddle(handlePaddleEvents)

    const openCustomCheckout = () => {
        try {
            const paddleSessionId = generateRandomId()
            localStorage.setItem('paddleSessionId', paddleSessionId) // We do this because we need to verify this identifier later on the 'VerifyPayment'
            paddleClient?.Checkout.open({
                transactionId,
                customData: { account: account._id, sessionId: paddleSessionId, user: user._id },
            })
            if (onCheckoutOpened) {
                onCheckoutOpened()
            }
        } catch (e) {
            handleError(e)
            toast.error(paymentErrorMessage(e, 'There was a problem opening the checkout. Please try again.'))
            console.error(e)
        }
    }

    const onUpgradeSubscription = async () => {
        try {

            const chosenPlan = chosenPlanToUpgrade.chosenPlan
            const chosenCycle = chosenPlanToUpgrade.chosenCycle
            const paddleSessionId = chosenPlanToUpgrade.paddleSessionId

            setChosenPlanToUpgrade(null)
            toggleVerifyPayment(true)
            await API.upgradeSubscription({ newPlanId: chosenPlan._id, billingCycle: chosenCycle, sessionId: paddleSessionId })
            togglePricingPlansDialog(false)

        } catch (e) {
            handleError(e)
            toast.error(paymentErrorMessage(e, 'We could not complete your upgrade. Please check your payment details and try again.'))
            console.error(e)
            toggleVerifyPayment(false)
        }
    }

    const openCheckout = async (chosenPlan, chosenCycle) => {
        try {

            // Custom checkout 
            if (transactionId) {
                openCustomCheckout()
                return
            }

            // Check if needs Shopify checkout (This logic should be away from here in a seprate service or something like that)
            if (isShopify) {
                const planId = chosenPlan._id
                // We set those to local storage to verify the plan id and cycle later on the 'VerifyShopifyPaymentPage'
                localStorage.setItem('shop', account.shopDomain)
                localStorage.setItem('planId', planId)
                // localStorage.setItem('billingCycle', chosenCycle)
                const callbackUrl = `${window.location.origin}/payment/shopify`
                const { data } = await API.initShopifyCheckout({ planId, callbackUrl, billingCycle: chosenCycle })
                window.location.replace(data.confirmationUrl)
                return
            }



            // --------- Continue normal flow with Paddle ---------


            // Regular flow
            let paddlePriceId
            const relevantBillingCyclesPrices = chosenPlan.providers?.paddle?.prices?.[chosenCycle]

            // Filter the prices to get the active price (that's way we can swtich on the DB the price id we need)
            if (relevantBillingCyclesPrices) {
                const activePrice = relevantBillingCyclesPrices.find(price => price.active)
                if (activePrice) {
                    paddlePriceId = activePrice.price_id
                }
            }

            if (!paddlePriceId) {
                // It's important for us to track this issue if it's occurred
                handleError(new Error('Missing plan price id'))
                AnalyticsService.track('missing-price-id-on-checkout', { plan: chosenPlan._id, cycle: chosenCycle })
                toast.error("There was an error initializing the checkout, please contact support")
                console.warn(`missing plan price id on plan - ${chosenPlan._id}. chosenCycle - ${chosenCycle}`)
                return
            }

            const paddleSessionId = generateRandomId()
            localStorage.setItem('paddleSessionId', paddleSessionId) // We do this because we need to verify this identifier later on the 'VerifyPayment'

            if (subscription?.tier && subscription?.plan && chosenPlan.tier > subscription?.tier) {
                setChosenPlanToUpgrade({ chosenPlan, chosenCycle, paddleSessionId })
                return
            }

            paddleClient?.Checkout.open({
                // settings: {
                // variant: "one-page"
                // displayMode: "inline"
                // },
                // transactionId: 'txn_01jnbtcntf3qke09twaaf4zvy5' - to create custom URL
                customData: { account: account._id, sessionId: paddleSessionId, user: user._id },
                items: [{ priceId: paddlePriceId }]
            })
            if (onCheckoutOpened) {
                onCheckoutOpened()
            }

        } catch (e) {
            handleError(e)
            toast.error(paymentErrorMessage(e, 'There was a problem starting the checkout. Please try again.'))
            console.error(e)
        }
    }

    const onCancelSubscription = async () => {
        try {
            setIsApproveCancelDialogOpen(false)
            setIsCancelingSubscription(true)
            await cancelSubscription(subscription._id)

            // Poll it the dumb way...
            for (let i = 0; i < 50; i++) {
                await new Promise(resolve => setTimeout(resolve, 1000))
                const { data } = await getSubscriptionOfAccount()
                if (data.status === 'free') {
                    setSubscription(data)
                    toast.info('Subscription Canceled Successfully')
                    AnalyticsService.track('subscription-canceled')
                    await syncEntitlements()
                    break
                }
            }

            if (onSubscriptionCancelled) {
                onSubscriptionCancelled()
            }

        } catch (e) {
            handleError(e)
            toast.error(paymentErrorMessage(e, 'We could not cancel your subscription. Please try again or contact support.'))
            console.error(e)
        } finally {
            setIsCancelingSubscription(false)
        }
    }

    useEffect(() => {
        if (autoOpenCustomCheckout && transactionId && paddleClient) {
            openCustomCheckout()
        }
    }, [autoOpenCustomCheckout, transactionId, paddleClient])

    // Custom is always last (right-most), then by order.
    const sortedPlans = plans
        .slice()
        .sort((a, b) => (Number(!!a.isCustom) - Number(!!b.isCustom)) || (a.order - b.order))

    // Grid keeps balanced rows: 2-per-row when it wraps (4 → 2+2, never 3+1),
    // all in one row on wide screens. Columns are sized to the card (not 1fr) and
    // the whole group is centered, so cards stay tight together, not spread out.
    // Literal classes so Tailwind can see + generate them.
    const largeColsByCount = {
        2: 'xl:grid-cols-[repeat(2,minmax(0,18rem))]',
        3: 'xl:grid-cols-[repeat(3,minmax(0,18rem))]',
        4: 'xl:grid-cols-[repeat(4,minmax(0,18rem))]',
        5: 'xl:grid-cols-[repeat(5,minmax(0,18rem))]',
    }
    const largeCols = largeColsByCount[Math.min(Math.max(sortedPlans.length, 2), 5)]

    return (
        <div className="flex flex-col">

            <Dialog open={isApproveCancelDialogOpen} onOpenChange={setIsApproveCancelDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Are you sure?</DialogTitle>
                        <DialogDescription>Approve you want to cancel your subscription</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <div className="flex justify-between gap-2">
                            <Button variant="outline" onClick={() => setIsApproveCancelDialogOpen(false)}>Keep Subscription</Button>
                            <Button variant="destructive" onClick={onCancelSubscription}>Cancel Subscription</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={chosenPlanToUpgrade} onOpenChange={() => setChosenPlanToUpgrade(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Upgrade</DialogTitle>
                        <DialogDescription>Approve you want to upgrade your subscription</DialogDescription>
                    </DialogHeader>

                    <div className="flex my-2">
                        <TypographySmall>Your are upgrading to "{chosenPlanToUpgrade?.chosenPlan?.name || ''}".</TypographySmall>
                    </div>


                    <DialogFooter>



                        <div className="flex justify-between gap-2">
                            <Button variant="outline" onClick={() => setChosenPlanToUpgrade(null)}>Keep Subscription</Button>
                            <Button onClick={onUpgradeSubscription}>Upgrade Subscription</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {
                isCancellingSubscription && (
                    <div className="fixed bg-white/85 w-full h-full top-0 left-0 z-50 flex flex-col items-center justify-center gap-2">
                        <CircleLoader />
                        Canceling Subscription...
                    </div>
                )
            }
            {/* Billing cycle toggle */}
            <div className="flex justify-center py-5">
                <div className="inline-flex items-center rounded-full border border-border bg-muted/50 p-1">
                    <button
                        type="button"
                        onClick={() => setCycle('monthly')}
                        className={cn(
                            'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                            cycle === 'monthly' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        Monthly
                    </button>
                    <button
                        type="button"
                        onClick={() => setCycle('annually')}
                        className={cn(
                            'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                            cycle === 'annually' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        Yearly
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">Save 20%</span>
                    </button>
                </div>
            </div>
            <div className={cn('grid grid-cols-1 justify-center gap-5 pb-2 sm:grid-cols-[repeat(2,minmax(0,18rem))]', largeCols)}>
                {
                    sortedPlans
                        .map((plan, index) => {


                            let ctaText = 'Subscribe'
                            let isDowngrade = false

                            if (plan._id === subscription?.plan) {
                                ctaText = 'Current'
                            }
                            if (subscription?.tier && plan.tier > subscription?.tier && !subscription?.inherited) {
                                ctaText = 'Upgrade'
                            }
                            if (plan.tier < subscription?.tier && !subscription?.inherited) {
                                if (isShopify) {
                                    ctaText = 'Downgrade'
                                } else {
                                    isDowngrade = true
                                    ctaText = 'Cancel to Downgrade'
                                }
                            }

                            if (plan.isCustom) {
                                ctaText = "Let's Talk"
                            }

                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="relative w-full"
                                    transition={{ duration: `0.${index + 1}` }}
                                >
                                    <PlanCard
                                        ctaText={ctaText}
                                        cycle={cycle}
                                        plan={plan}
                                        currentSubscription={subscription}
                                        onPlanChosen={openCheckout}
                                        isDowngrade={isDowngrade}
                                        specielOfferText="Cancel Anytime!"
                                    />
                                </motion.div>
                            )
                        })
                }
            </div>
            <div className="mt-7 flex flex-col items-center gap-3 pb-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Have questions?</span>
                    <button
                        type="button"
                        onClick={() => window.open('https://calendar.notion.so/meet/rdahan/iq2y63up0', '_blank')}
                        className="inline-flex items-center gap-1.5 font-medium text-foreground transition-colors hover:text-primary"
                    >
                        <Icon icon="solar:chat-round-line-linear" className="h-4 w-4" />
                        Talk to us
                    </button>
                </div>
                {subscription && subscription.status !== 'free' && (
                    <button
                        type="button"
                        onClick={() => setIsApproveCancelDialogOpen(true)}
                        className="text-xs text-muted-foreground transition-colors hover:text-destructive"
                    >
                        Cancel subscription
                    </button>
                )}
            </div>
        </div>

    )
}